import { stripHtml } from "../exports/functions";
import {
  fetchAboutBot,
  fetchAboutHistory,
  fetchAboutIntroduction,
  fetchAboutJoinUs,
  fetchAboutWhoUnepalFor,
  fetchAboutWhyUnepal,
  fetchBotDescription,
  fetchCoreValuesPoints,
  fetchCoreValuesStatement,
  fetchMission,
  fetchVision,
} from "../api/AboutApi";
import {
  fetchAcademicsMppBackground,
  fetchAcademicsAdmissionOpen,
  fetchAcademicsApplicationEligibility,
  fetchAcademicsCurriculum,
  fetchAcademicsFeeScholarship,
  fetchAcademicsFirstCohort,
  fetchAcademicsKeyFeatures,
  fetchAcademicsMppDetailsBanner,
  fetchAcademicsWhyMpp,
} from "../api/GraduateProgramsApi";
import {
  fetchBuildingForFutureDetails,
  fetchMultiYearCommitmentsTab,
  fetchOneTimeDonationsTab,
  fetchPartnershipsTab,
} from "../api/BuildingForFutureApi";
import { fetchContactDetails } from "../api/ContactApi";
import {
  fetchAcademicsMenuCourses,
  fetchAcademicsMenuPrograms,
  fetchChancellorMessage,
  fetchHomeAboutUs,
  fetchHomeBanner,
  fetchHomeUniversity,
  fetchNewsEvents,
  fetchViceChancellorMessage,
} from "../api/HomeApi";
import {
  fetchJoinAsFacultyDetails,
  fetchAreasMajorsTab,
  fetchWhoIsUNepalFor,
  fetchWhyJoinUNepal,
} from "../api/JoinAsFacultyApi";
import { fetchNews, fetchEvents } from "../api/NewsEventsApi";
import { fetchEnglishFaqs, fetchNepaliFaqs } from "../api/FaqApi";
import {
  fetchOurBrochure,
  fetchUndergraduateBrochure,
  fetchMPPBrochure,
} from "../api/BrochureApi";
import { fetchFooterDetails } from "../api/FooterApi";
import { CompanyDetails } from "../api/CompanyDetailsApi";
import {
  fetchSastBackground,
  fetchSastBackgroundDetails,
  fetchSastDetailsBanner,
  fetchSastHighlightsTab,
  fetchSastCoreCurriculumTab,
  fetchSastAreasOfStudyTab,
  fetchSast4YearPlanTab,
  fetchCurriculumDevelopmentTeam,
  // fetchAdmissionPolicy,
  // fetchScholarshipsAndFinancial,
} from "../api/UndergraduateProgramsApi";
import {
  fetchVacancies,
  fetchAcademicVacancies,
  fetchDownloadForms,
} from "../api/VacanciesApi";

export interface SearchItem {
  id: string;
  title: string;
  description: string;
  path: string;
  category: string;
  content?: string;
}

type SearchSourceConfig = {
  source: string;
  fetcher: () => Promise<any>;
  basePath: string;
  category: string;
  titleFields?: string[];
  descriptionFields?: string[];
};

const titleFieldCandidates = [
  "title",
  "name",
  "heading",
  "headline",
  "sub_title",
  "subtitle",
  "question",
  "label",
  "name",
  "subject",
  "position",
];

const descriptionFieldCandidates = [
  "description",
  "summary",
  "text",
  "content",
  "details",
  "body",
  "answer",
  "caption",
  "about",
  "statement",
  "message",
  "subtitle",
  "sub_title",
];

const linkFieldNames = [
  "url",
  "href",
  "src",
  "image",
  "img",
  "photo",
  "file",
  "media",
  "video",
  "link",
  "icon",
  "logo",
  "thumbnail",
  "avatar",
  "pdf",
  "button",
  "download",
];

const isLikelyLinkKey = (key?: string) => {
  if (!key) return false;
  const normalized = key.toLowerCase();
  return linkFieldNames.some((linkKey) => normalized.includes(linkKey));
};

const normalizeText = (value: any): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    const text = stripHtml(value);
    return text.replace(/\s+/g, " ").trim();
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeText(item))
      .filter(Boolean)
      .join(" ");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([key, _]) => !isLikelyLinkKey(key))
      .map(([key, item]) => normalizeText(item))
      .filter(Boolean)
      .join(" ");
  }
  return "";
};

const pickFieldText = (
  record: Record<string, any>,
  candidates: string[],
): string => {
  for (const field of candidates) {
    const value = record[field];
    const text = normalizeText(value);
    if (text) return text;
  }
  return "";
};

const buildRecordPath = (record: any, config: SearchSourceConfig) => {
  if (record?.slug && typeof record.slug === "string") {
    return `${config.basePath}/${encodeURIComponent(record.slug)}`;
  }

  if (
    config.source === "SAST Courses" &&
    typeof record?.program_name === "string" &&
    typeof record?.name === "string"
  ) {
    return `/sast/${encodeURIComponent(record.program_name)}/${encodeURIComponent(record.name)}`;
  }

  if (
    config.source === "Academic Programs" &&
    typeof record?.title === "string"
  ) {
    return "/sast";
  }

  return config.basePath;
};

const buildSearchItem = (
  record: any,
  index: number,
  config: SearchSourceConfig,
): SearchItem | null => {
  const content = normalizeText(record);
  if (!content) return null;

  const titleFromRecord = pickFieldText(
    record,
    config.titleFields ?? titleFieldCandidates,
  );
  const descriptionFromRecord = pickFieldText(
    record,
    config.descriptionFields ?? descriptionFieldCandidates,
  );

  const title = titleFromRecord || `${config.source}`;
  const description =
    descriptionFromRecord ||
    content.slice(0, 140) ||
    `${config.source} content`;

  const path = buildRecordPath(record, config);

  return {
    id: `${config.source}-${index}-${title}`,
    title,
    description,
    path,
    category: config.category,
    content,
  };
};

export const buildSearchIndex = async (): Promise<SearchItem[]> => {
  const configs: SearchSourceConfig[] = [
    {
      source: "Home Banner",
      fetcher: fetchHomeBanner,
      basePath: "/",
      category: "Home",
    },
    {
      source: "Home About",
      fetcher: fetchHomeAboutUs,
      basePath: "/",
      category: "Home",
    },
    {
      source: "Chancellor Message",
      fetcher: fetchChancellorMessage,
      basePath: "/",
      category: "Home",
    },
    {
      source: "Vice Chancellor Message",
      fetcher: fetchViceChancellorMessage,
      basePath: "/",
      category: "Home",
    },
    {
      source: "University Section",
      fetcher: fetchHomeUniversity,
      basePath: "/",
      category: "Home",
    },
    {
      source: "Home News and Events",
      fetcher: fetchNewsEvents,
      basePath: "/",
      category: "Home",
    },
    {
      source: "About Introduction",
      fetcher: fetchAboutIntroduction,
      basePath: "/about",
      category: "About",
    },
    {
      source: "Why UNEPAL",
      fetcher: fetchAboutWhyUnepal,
      basePath: "/about",
      category: "About",
    },
    {
      source: "Who is UNEPAL For",
      fetcher: fetchAboutWhoUnepalFor,
      basePath: "/about",
      category: "About",
    },
    {
      source: "Join Us",
      fetcher: fetchAboutJoinUs,
      basePath: "/about",
      category: "About",
    },
    {
      source: "Vision Statement",
      fetcher: fetchVision,
      basePath: "/about",
      category: "About",
    },
    {
      source: "Mission Statement",
      fetcher: fetchMission,
      basePath: "/about",
      category: "About",
    },
    {
      source: "Core Values Statement",
      fetcher: fetchCoreValuesStatement,
      basePath: "/about",
      category: "About",
    },
    {
      source: "Core Values Points",
      fetcher: fetchCoreValuesPoints,
      basePath: "/about",
      category: "About",
    },
    {
      source: "History",
      fetcher: fetchAboutHistory,
      basePath: "/about",
      category: "About",
    },
    {
      source: "Board of Trustees",
      fetcher: fetchAboutBot,
      basePath: "/about",
      category: "About",
    },
    {
      source: "Board Description",
      fetcher: fetchBotDescription,
      basePath: "/about",
      category: "About",
    },
    {
      source: "SAST Background",
      fetcher: fetchSastBackground,
      basePath: "/sast",
      category: "Undergraduate",
    },
    {
      source: "SAST Details Banner",
      fetcher: fetchSastDetailsBanner,
      basePath: "/sast",
      category: "Undergraduate",
    },
    {
      source: "SAST Background Details",
      fetcher: fetchSastBackgroundDetails,
      basePath: "/sast",
      category: "Undergraduate",
    },
    {
      source: "SAST Highlights",
      fetcher: fetchSastHighlightsTab,
      basePath: "/sast",
      category: "Undergraduate",
    },
    {
      source: "SAST Core Curriculum",
      fetcher: fetchSastCoreCurriculumTab,
      basePath: "/sast",
      category: "Undergraduate",
    },
    {
      source: "SAST Areas of Study",
      fetcher: fetchSastAreasOfStudyTab,
      basePath: "/sast",
      category: "Undergraduate",
    },
    {
      source: "4 Year Plan",
      fetcher: fetchSast4YearPlanTab,
      basePath: "/sast",
      category: "Undergraduate",
    },
    {
      source: "Curriculum Development Team",
      fetcher: fetchCurriculumDevelopmentTeam,
      basePath: "/sast",
      category: "Undergraduate",
    },
    // {
    //   source: "Admission Policy",
    //   fetcher: fetchAdmissionPolicy,
    //   basePath: "/application-form",
    //   category: "Application",
    // },
    // {
    //   source: "Scholarships and Financial Aid",
    //   fetcher: fetchScholarshipsAndFinancial,
    //   basePath: "/application-form",
    //   category: "Application",
    // },
    {
      source: "MPP Background",
      fetcher: fetchAcademicsMppBackground,
      basePath: "/mpp",
      category: "Graduate",
    },
    {
      source: "MPP Details Banner",
      fetcher: fetchAcademicsMppDetailsBanner,
      basePath: "/mpp",
      category: "Graduate",
    },
    {
      source: "Why MPP",
      fetcher: fetchAcademicsWhyMpp,
      basePath: "/mpp",
      category: "Graduate",
    },
    {
      source: "MPP Key Features",
      fetcher: fetchAcademicsKeyFeatures,
      basePath: "/mpp",
      category: "Graduate",
    },
    {
      source: "MPP Admission Open",
      fetcher: fetchAcademicsAdmissionOpen,
      basePath: "/mpp",
      category: "Graduate",
    },
    {
      source: "MPP Curriculum",
      fetcher: fetchAcademicsCurriculum,
      basePath: "/mpp",
      category: "Graduate",
    },
    {
      source: "MPP Fee & Scholarship",
      fetcher: fetchAcademicsFeeScholarship,
      basePath: "/mpp",
      category: "Graduate",
    },
    {
      source: "MPP Application Eligibility",
      fetcher: fetchAcademicsApplicationEligibility,
      basePath: "/mpp",
      category: "Graduate",
    },
    {
      source: "MPP First Cohort",
      fetcher: fetchAcademicsFirstCohort,
      basePath: "/mpp",
      category: "Graduate",
    },
    {
      source: "Join As Faculty",
      fetcher: fetchJoinAsFacultyDetails,
      basePath: "/community-faculty",
      category: "Community Faculty",
    },
    {
      source: "Areas and Majors",
      fetcher: fetchAreasMajorsTab,
      basePath: "/community-faculty",
      category: "Community Faculty",
    },
    {
      source: "Who is UNEPAL For",
      fetcher: fetchWhoIsUNepalFor,
      basePath: "/community-faculty",
      category: "Community Faculty",
    },
    {
      source: "Why Join UNEPAL",
      fetcher: fetchWhyJoinUNepal,
      basePath: "/community-faculty",
      category: "Community Faculty",
    },
    {
      source: "Building for the Future",
      fetcher: fetchBuildingForFutureDetails,
      basePath: "/community-university",
      category: "Community University",
    },
    {
      source: "One-Time Donations",
      fetcher: fetchOneTimeDonationsTab,
      basePath: "/community-university",
      category: "Community University",
    },
    {
      source: "Multi-Year Commitments",
      fetcher: fetchMultiYearCommitmentsTab,
      basePath: "/community-university",
      category: "Community University",
    },
    {
      source: "Partnerships",
      fetcher: fetchPartnershipsTab,
      basePath: "/community-university",
      category: "Community University",
    },
    {
      source: "Contact Details",
      fetcher: fetchContactDetails,
      basePath: "/contact",
      category: "Contact",
    },
    {
      source: "News Items",
      fetcher: fetchNews,
      basePath: "/news",
      category: "News",
    },
    {
      source: "Event Items",
      fetcher: fetchEvents,
      basePath: "/events",
      category: "Events",
    },
    {
      source: "English FAQs",
      fetcher: fetchEnglishFaqs,
      basePath: "/faqs",
      category: "Faqs",
    },
    {
      source: "Nepali FAQs",
      fetcher: fetchNepaliFaqs,
      basePath: "/faqs",
      category: "Faqs",
    },
    {
      source: "Vacancies",
      fetcher: fetchVacancies,
      basePath: "/vacancy",
      category: "Vacancies",
    },
    {
      source: "Academic Vacancies",
      fetcher: fetchAcademicVacancies,
      basePath: "/academic-vacancies",
      category: "Vacancies",
    },
    {
      source: "Download Forms",
      fetcher: fetchDownloadForms,
      basePath: "/vacancy",
      category: "Vacancies",
    },
    {
      source: "Academic Programs",
      fetcher: fetchAcademicsMenuPrograms,
      basePath: "/sast",
      category: "Undergraduate",
    },
    {
      source: "SAST Courses",
      fetcher: fetchAcademicsMenuCourses,
      basePath: "/sast",
      category: "Undergraduate",
      titleFields: ["name", "title"],
      descriptionFields: ["program_name", "description", "details"],
    },
    {
      source: "Our Brochure",
      fetcher: fetchOurBrochure,
      basePath: "/",
      category: "Resource",
    },
    {
      source: "Undergraduate Brochure",
      fetcher: fetchUndergraduateBrochure,
      basePath: "/sast",
      category: "Resource",
    },
    {
      source: "MPP Brochure",
      fetcher: fetchMPPBrochure,
      basePath: "/mpp",
      category: "Resource",
    },
    {
      source: "Footer Details",
      fetcher: fetchFooterDetails,
      basePath: "/",
      category: "Contact",
    },
    {
      source: "Company Details",
      fetcher: CompanyDetails,
      basePath: "/",
      category: "About",
    },
  ];

  const loadedItems: SearchItem[] = [];

  const results = await Promise.allSettled(
    configs.map(async (config) => {
      const payload = await config.fetcher();
      return { config, payload };
    }),
  );

  results.forEach((result) => {
    if (result.status !== "fulfilled" || !result.value?.payload) return;

    const { config, payload } = result.value;
    const records = Array.isArray(payload) ? payload : [payload];

    records.forEach((record, index) => {
      const item = buildSearchItem(record, index, config);

      if (item) {
        loadedItems.push(item);
      }
    });
  });

  return loadedItems;
};
